// src/pages/buyer/ReviewsPage.jsx
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Star, MessageSquare, PenLine, Search, ThumbsUp,
  CheckCircle, Clock, XCircle, ChevronLeft, ChevronRight,
} from 'lucide-react'
import { reviewsApi } from '@/api/services'
import { Card, CardBody, Button, Badge, StatusBadge, PageSpinner, Empty } from '@/components/common/ui'

// ── Star Picker ──────────────────────────────────────────────────────────
function StarPicker({ value, onChange, size = 20 }) {
  const [hover, setHover] = useState(0)
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" onClick={() => onChange(n)}
          onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
          className="transition-transform hover:scale-110">
          <Star size={size} className={n <= (hover || value)
            ? 'fill-yellow-400 text-yellow-400'
            : 'text-gray-200'
          } />
        </button>
      ))}
    </div>
  )
}

function StarDisplay({ rating, size = 12 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(n => (
        <Star key={n} size={size} className={n <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'} />
      ))}
    </div>
  )
}

function SubRatingInput({ label, value, onChange }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-muted w-28 flex-shrink-0">{label}</span>
      <StarPicker value={value} onChange={onChange} size={16} />
    </div>
  )
}

const TABS = [
  { key: 'mine',  label: 'My Reviews', icon: MessageSquare },
  { key: 'write', label: 'Write Review', icon: PenLine },
]

const FORM_DEFAULT = {
  reviewable_type: 'product', reviewable_id: '', order_id: '',
  rating: 0, title: '', body: '',
  quality_score: 0, communication_score: 0, delivery_score: 0,
}

export default function ReviewsPage() {
  const [searchParams] = useSearchParams()
  const qc = useQueryClient()

  // Pre-fill from URL params (from OrderDetailPage "Write Review" button)
  const prefill = {
    ...FORM_DEFAULT,
    order_id: searchParams.get('order') || '',
    reviewable_id: searchParams.get('product') || '',
    reviewable_type: searchParams.get('type') || 'product',
  }

  const [tab, setTab] = useState(searchParams.get('order') ? 'write' : 'mine')
  const [form, setForm] = useState(prefill)
  const [errors, setErrors] = useState({})
  const [page, setPage] = useState(1)

  // ── Queries ──────────────────────────────────────────────────────
  const { data: myData, isLoading } = useQuery({
    queryKey: ['my-reviews', page],
    queryFn: () => reviewsApi.myReviews({ page }).then(r => r.data),
    enabled: tab === 'mine',
  })

  const reviews = myData?.data?.data ?? myData?.data ?? []
  const lastPage = myData?.data?.last_page ?? myData?.last_page ?? 1

  // ── Submit mutation ──────────────────────────────────────────────
  const submitMut = useMutation({
    mutationFn: (data) => reviewsApi.submit(data),
    onSuccess: () => {
      setForm(FORM_DEFAULT)
      setErrors({})
      qc.invalidateQueries({ queryKey: ['my-reviews'] })
      setTab('mine')
    },
    onError: (e) => {
      const errs = e.response?.data?.errors ?? {}
      setErrors(Object.keys(errs).length ? errs : { general: e.response?.data?.message || 'Submission failed.' })
    },
  })

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const handleSubmit = () => {
    setErrors({})
    if (!form.rating) { setErrors({ rating: 'Please select a rating.' }); return }
    if (!form.reviewable_id) { setErrors({ reviewable_id: 'Product or supplier ID is required.' }); return }

    const payload = { ...form }
    if (!payload.order_id) delete payload.order_id
    if (!payload.quality_score) delete payload.quality_score
    if (!payload.communication_score) delete payload.communication_score
    if (!payload.delivery_score) delete payload.delivery_score

    submitMut.mutate(payload)
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-display font-bold flex items-center gap-2">
          <Star size={20} className="text-pink" /> Reviews
        </h1>
        <p className="text-sm text-muted mt-0.5">Share your experience and help the community</p>
      </div>

      {/* Tab bar */}
      <div className="bg-white border border-border rounded-2xl p-3 mb-5">
        <div className="flex items-center gap-1">
          {TABS.map(t => {
            const Icon = t.icon
            const isActive = tab === t.key
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                  isActive ? 'bg-pink text-white' : 'text-muted hover:bg-surface'
                }`}>
                <Icon size={13} /> {t.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* ── MY REVIEWS ── */}
      {tab === 'mine' && (
        <>
          {isLoading && <PageSpinner />}

          {!isLoading && reviews.length === 0 && (
            <Empty icon="⭐" title="No reviews yet"
              description="After receiving an order, you can review the product or supplier."
              action={<Button onClick={() => setTab('write')}>Write Your First Review</Button>}
            />
          )}

          {!isLoading && reviews.length > 0 && (
            <div className="space-y-3">
              {reviews.map(r => (
                <Card key={r.id}>
                  <CardBody>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <StarDisplay rating={r.rating} />
                        <StatusBadge status={r.status} />
                        {r.is_verified_purchase && (
                          <Badge variant="green" className="text-[10px]">
                            <CheckCircle size={10} className="mr-0.5" /> Verified Purchase
                          </Badge>
                        )}
                      </div>
                      <span className="text-[11px] text-muted whitespace-nowrap">
                        {r.created_at && new Date(r.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>

                    {r.title && <p className="text-sm font-bold text-gray-900 mb-1">{r.title}</p>}
                    {r.body && <p className="text-xs text-gray-600 leading-relaxed mb-2">{r.body}</p>}

                    {/* Sub-ratings */}
                    {(r.quality_score || r.communication_score || r.delivery_score) && (
                      <div className="flex gap-4 text-[11px] text-muted mb-2">
                        {r.quality_score > 0 && <span>Quality: <strong>{r.quality_score}/5</strong></span>}
                        {r.communication_score > 0 && <span>Communication: <strong>{r.communication_score}/5</strong></span>}
                        {r.delivery_score > 0 && <span>Delivery: <strong>{r.delivery_score}/5</strong></span>}
                      </div>
                    )}

                    <div className="text-[11px] text-muted">
                      {r.reviewable_type === 'App\\Models\\Product' ? 'Product' : r.reviewable_type === 'App\\Models\\Supplier' ? 'Supplier' : r.reviewable_type} #{r.reviewable_id}
                      {r.helpful_count > 0 && (
                        <span className="ml-3 inline-flex items-center gap-1">
                          <ThumbsUp size={10} /> {r.helpful_count} found helpful
                        </span>
                      )}
                    </div>

                    {/* Supplier Reply */}
                    {r.supplier_reply && (
                      <div className="mt-3 p-3 bg-green-50 border-l-3 border-green-500 rounded-lg">
                        <p className="text-[11px] font-bold text-green-700 mb-1">Supplier Reply</p>
                        <p className="text-xs text-gray-700">{r.supplier_reply}</p>
                      </div>
                    )}
                  </CardBody>
                </Card>
              ))}
            </div>
          )}

          {/* Pagination */}
          {lastPage > 1 && (
            <div className="flex items-center justify-center gap-3 mt-6">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="flex items-center gap-1 px-3 py-2 rounded-xl border border-border text-xs font-bold disabled:opacity-40 hover:border-pink transition-colors">
                <ChevronLeft size={14} /> Prev
              </button>
              <span className="text-xs text-muted font-semibold">Page {page} of {lastPage}</span>
              <button onClick={() => setPage(p => Math.min(lastPage, p + 1))} disabled={page === lastPage}
                className="flex items-center gap-1 px-3 py-2 rounded-xl border border-border text-xs font-bold disabled:opacity-40 hover:border-pink transition-colors">
                Next <ChevronRight size={14} />
              </button>
            </div>
          )}
        </>
      )}

      {/* ── WRITE REVIEW ── */}
      {tab === 'write' && (
        <Card>
          <CardBody>
            {submitMut.isSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-5 flex items-center gap-2">
                <CheckCircle size={16} className="text-green-600" />
                <span className="text-sm font-bold text-green-800">Review submitted! Pending moderation.</span>
              </div>
            )}

            <div className="space-y-5">
              {/* Type + ID */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-1.5">Reviewing</label>
                  <select value={form.reviewable_type} onChange={e => setF('reviewable_type', e.target.value)}
                    className="input w-full">
                    <option value="product">Product</option>
                    <option value="supplier">Supplier</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-1.5">
                    {form.reviewable_type === 'product' ? 'Product' : 'Supplier'} ID *
                  </label>
                  <input type="number" value={form.reviewable_id} onChange={e => setF('reviewable_id', e.target.value)}
                    placeholder="e.g. 42"
                    className={`input w-full ${errors.reviewable_id ? 'border-red-400' : ''}`} />
                  {errors.reviewable_id && <p className="text-red-500 text-[11px] mt-1">{errors.reviewable_id}</p>}
                </div>
              </div>

              {/* Order ID */}
              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-1.5">
                  Order ID <span className="text-muted font-normal normal-case">(links to verified purchase)</span>
                </label>
                <input type="number" value={form.order_id} onChange={e => setF('order_id', e.target.value)}
                  placeholder="e.g. 123" className="input w-full max-w-xs" />
              </div>

              {/* Overall Rating */}
              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Overall Rating *</label>
                <StarPicker value={form.rating} onChange={v => setF('rating', v)} size={28} />
                {form.rating > 0 && (
                  <p className="text-xs text-muted mt-1">
                    {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][form.rating]}
                  </p>
                )}
                {errors.rating && <p className="text-red-500 text-[11px] mt-1">{errors.rating}</p>}
              </div>

              {/* Title */}
              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-1.5">Title</label>
                <input value={form.title} onChange={e => setF('title', e.target.value)}
                  placeholder="Summarise your experience…" className="input w-full" maxLength={150} />
              </div>

              {/* Body */}
              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-1.5">Your Review</label>
                <textarea value={form.body} onChange={e => setF('body', e.target.value)} rows={4}
                  placeholder="Tell others about quality, delivery, communication…"
                  className="input w-full resize-y" />
              </div>

              {/* Sub-ratings */}
              <div className="bg-surface rounded-xl p-4">
                <p className="text-xs font-bold text-gray-900 mb-3">Sub-Ratings (optional)</p>
                <div className="space-y-2">
                  <SubRatingInput label="Product Quality" value={form.quality_score} onChange={v => setF('quality_score', v)} />
                  <SubRatingInput label="Communication" value={form.communication_score} onChange={v => setF('communication_score', v)} />
                  <SubRatingInput label="Delivery Speed" value={form.delivery_score} onChange={v => setF('delivery_score', v)} />
                </div>
              </div>

              {errors.general && <p className="text-red-500 text-sm font-semibold">{errors.general}</p>}

              <Button onClick={handleSubmit} loading={submitMut.isPending}>
                <Star size={14} /> Submit Review
              </Button>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  )
}
